<script setup lang="ts">
import { onMounted, ref, watch, computed } from 'vue';
import { useCalendarBulkAttendanceStore, useCalendarStore } from '@/store';
import FullCalendar from '@fullcalendar/vue3';
import { CalendarOptions, EventClickArg } from '@fullcalendar/core';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import CalendarBulkAttendance from './CalendarBulkAttendance.vue';
import { CALENDAR_COLOR, EMIT, PERMISSIONS } from '@/enum';
import { can, modifiedRRulePlugin, useDateTime, usePersonTypes } from '@/composables';
import Provider from '@/components/Provider';

const emit = defineEmits<{
  (eventName: EMIT.DOCUMENT_EVENT, info: EventClickArg): void;
  (eventName: EMIT.EDIT_EVENT, info: EventClickArg): void;
  (eventName: EMIT.MOUNTED, info: string): void;
}>();
const calendar = ref();
const calendarStore = useCalendarStore();
const calendarBulkAttendanceStore = useCalendarBulkAttendanceStore();
const today = new Date();

defineExpose({ calendar });

onMounted(() => {
  setCalendarEvents();
});

const setCalendarEvents = () => {
  calendarOptions.value.events = calendarStore.events ? calendarStore.events : [];
};

watch(() => calendarStore.events, setCalendarEvents);

const calendarOptions = ref<CalendarOptions>({
  plugins: [listPlugin, interactionPlugin, modifiedRRulePlugin],
  initialView: 'listWeek',
  headerToolbar: false,
  listDayFormat: { day: 'numeric' },
  dayHeaderContent: (arg) =>
    calendarBulkAttendanceStore.defineBulkAttendance(
      arg,
      can([PERMISSIONS.THERAPY_RESULTS_BULK_UPDATE]) && !usePersonTypes.isCaseManager(),
    ),
  eventClassNames: (arg) => calendarStore.defineEventsClassNames(arg),
  listDaySideFormat: false,
  stickyHeaderDates: false,
  weekends: true,
  nowIndicator: true,
  height: '100%',
  eventBackgroundColor: CALENDAR_COLOR.EVENT_BACKGROUND_COLOR,
  eventBorderColor: CALENDAR_COLOR.EVENT_BORDER_COLOR,
  eventTextColor: CALENDAR_COLOR.EVENT_TEXT_COLOR,
  eventMinHeight: 24,
  events: [],

  eventClick: (info: EventClickArg) => {
    const isDocumented = info.event._def.extendedProps.eventStatus === 'documented';
    const isInPast = (info.event.start as Date) < new Date();
    const isCaseloadManagement = calendarStore.isCaseloadManagement(info.event._def.extendedProps.eventType);
    const showDocumentEvent = isDocumented || isInPast;
    const isCaseloadAndInCurrentMonth =
      isCaseloadManagement && useDateTime().isSameMonthAndYear(info.event.start as Date);

    if (showDocumentEvent && !isCaseloadAndInCurrentMonth) {
      emit(EMIT.DOCUMENT_EVENT, info);
    } else {
      emit(EMIT.EDIT_EVENT, info);
    }
  },
  viewDidMount: () => emit(EMIT.MOUNTED, 'list'),

  eventDidMount: (info) => {
    const eventTimeTd = info.el.getElementsByClassName('fc-list-event-time')[0];

    const start = useDateTime().formatTimeByTimezone(
      info.event.start as Date,
      info.event.extendedProps.hostTimezoneName,
    );

    const end = useDateTime().formatTimeByTimezone(info.event.end as Date, info.event.extendedProps.hostTimezoneName);

    if (eventTimeTd) {
      //need to catch it on the next cycle because it still loads the orig text
      setTimeout(() => {
        eventTimeTd.innerHTML = `${start} - ${end}  ${info.event.extendedProps.hostTimezone}`;
      }, 0);
    }
  },
});

const isClinician = computed(() => usePersonTypes.isClinician());
</script>

<template>
  <div class="calendar-page-content">
    <div class="calendar-page-list-view pr-4 w-full">
      <CalendarBulkAttendance
        v-if="
          can([PERMISSIONS.THERAPY_RESULTS_BULK_UPDATE]) &&
          !usePersonTypes.isCaseManager() &&
          calendarBulkAttendanceStore.displayBulkAttendance
        "
        @cancel="calendarBulkAttendanceStore.onBulkAttendanceCancel"
      />
      <FullCalendar
        ref="calendar"
        :options="calendarOptions"
        :class="'today-is-' + today.toLocaleDateString('en-us', { weekday: 'long' })"
      >
        <template #eventContent="arg">
          <div class="fc-event-main-frame w-full">
            <div class="fc-event-title-container align-items-center">
              <div class="fc-event-title">
                <div class="flex grid w-full">
                  <div class="flex col-6">
                    <div class="title w-full flex">
                      {{ arg.event.title }}
                    </div>
                  </div>
                  <div v-if="!isClinician" class="col-6 flex p-1">
                    <Provider
                      :discipline-code="arg.event.extendedProps.discipline_code"
                      :service-provider-name="arg.event.extendedProps.classroom.service_provider_name"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </FullCalendar>
    </div>
  </div>
</template>

<style lang="scss" scoped>
// eslint-disable-next-line vue/block-tag-newline
@import '@/theme/extensions/calendar';
.calendar-page-list-view {
  padding: 1.5rem;

  .fc-event-title-container {
    min-width: 500px;
  }

  @media (max-width: 768px) {
    padding: 1rem;
  }
}
</style>
